/**
 * Browser utilities for SSR-safe access to window APIs
 */

const isBrowser = typeof window !== "undefined";

function addEventListener<K extends keyof WindowEventMap>(
  type: K,
  listener: (this: Window, ev: WindowEventMap[K]) => any,
  options?: boolean | AddEventListenerOptions,
) {
  if (!isBrowser) return;
  window.addEventListener(type, listener, options);
}

function removeEventListener<K extends keyof WindowEventMap>(
  type: K,
  listener: (this: Window, ev: WindowEventMap[K]) => any,
  options?: boolean | EventListenerOptions,
) {
  if (!isBrowser) return;
  window.removeEventListener(type, listener, options);
}

// DEVNOTE: matches the Dockerfile's `ARG COMMIT=local` and .env.example. This must never
// be empty: it is passed to Bugsnag as appVersion, and Bugsnag rejects "" as invalid
// configuration. index.html sets the global in a browser, so it is only absent where that
// document never ran (unit tests, SSR).
const UNKNOWN_APP_VERSION = "local";

function getAppVersion(): string {
  if (!isBrowser) return UNKNOWN_APP_VERSION;

  // Deliberately `||`, not `??` — an empty string means "not stamped", same as absent.
  return (window as any).FONS_APP_VERSION_COMMIT || UNKNOWN_APP_VERSION;
}

function getDocumentBody() {
  if (!isBrowser) return undefined;
  return window.document.body;
}

function getDimensions() {
  if (!isBrowser) return { width: 0, height: 0, outerWidth: 0, outerHeight: 0 };
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
  };
}

function getGtag() {
  if (!isBrowser) return null;
  return (window as any).gtag;
}

function getLocation() {
  if (!isBrowser) return null;
  return window.location;
}

function getLocationSearch() {
  return getLocation()?.search || "";
}

function getScreenPosition() {
  if (!isBrowser) return { x: 0, y: 0 };
  return {
    x: window.screenX || window.screenLeft || 0,
    y: window.screenY || window.screenTop || 0,
  };
}

function isInIframe() {
  if (!isBrowser) return false;

  try {
    return window.parent !== window.self;
  } catch {
    // security exception indicates frame
    return true;
  }
}

function isStandalone() {
  return isBrowser && "standalone" in window.navigator && !!(window.navigator as any).standalone;
}

function navigate(url: string) {
  if (!isBrowser) return;
  window.location.href = url;
}

function openWindow(url: string, target?: string, features?: string) {
  if (!isBrowser) return null;
  return window.open(url, target, features);
}

function reload() {
  if (!isBrowser) return;
  window.location.reload();
}

export const Browser = {
  addEventListener,
  getAppVersion,
  getDimensions,
  getDocumentBody,
  getLocation,
  getLocationSearch,
  getGtag,
  getScreenPosition,
  isInIframe,
  isStandalone,
  navigate,
  openWindow,
  reload,
  removeEventListener,
};
